import { ETelemetryTopOf, RequestTimelinePoint } from 'multirpc-sdk';
import { createSelector, weakMapMemoize } from 'reselect';
import { ChainID } from '@ankr.com/chains-list';

import { RootState } from 'store';
import { deepEqulityCheck } from 'modules/common/utils/deepEqualityCheck';
import { mapSharkChainToChainPath } from 'modules/chains/utils/mapSharkChainNameToChainPath';
import { selectAllPathsByChainId } from 'modules/chains/store/selectors';

import {
  BlockchainsCountsMap,
  CountriesCountsMap,
  UsageTimelineMap,
} from '../../types';
import {
  IFetchPrivateStatsParams,
  selectPrivateStats,
} from '../../actions/fetchPrivateStats';
import { aggregateBlockchainsCountsByChainID } from '../../utils/aggregateBlockchainsCountsByChainID';
import { getTopElementsById } from '../../utils/getTopElementsById';
import { sumBlockchainsCountsByChainPaths } from '../../utils/sumBlockchainsCountsByChainPaths';

const usageTimelineMapFallback: UsageTimelineMap = {};
const timelineFallback: RequestTimelinePoint[] = [];

export const selectPrivateAPIKeysCounts = createSelector(
  selectPrivateStats,
  ({ tops }) =>
    getTopElementsById({
      tops,
      id: ETelemetryTopOf.API_KEY,
    }),
);

export const selectPrivateBlockchainsCounts = createSelector(
  selectPrivateStats,
  ({ tops }) =>
    getTopElementsById({
      tops,
      id: ETelemetryTopOf.BLOCKCHAIN,
    }),
);

export const selectPrivateCountriesCounts = createSelector(
  selectPrivateStats,
  ({ tops }) =>
    getTopElementsById({
      tops,
      id: ETelemetryTopOf.COUNTRY,
    }),
);

export const selectPrivateErrorsCounts = createSelector(
  selectPrivateStats,
  ({ tops }) =>
    getTopElementsById({
      tops,
      id: ETelemetryTopOf.ERROR,
    }),
);

export const selectIPsCounts = createSelector(selectPrivateStats, ({ tops }) =>
  getTopElementsById({
    tops,
    id: ETelemetryTopOf.IP,
  }),
);

export const selectPrivateMethodsCounts = createSelector(
  selectPrivateStats,
  ({ tops }) =>
    getTopElementsById({
      tops,
      id: ETelemetryTopOf.METHOD,
    }),
);

export const selectMonthlyUsageCounts = createSelector(
  selectPrivateStats,
  ({ tops }) =>
    getTopElementsById({
      tops,
      id: ETelemetryTopOf.MONTHLY_USAGE,
    }),
);

export const selectPrivateBlockchainsCountsMap = createSelector(
  selectPrivateBlockchainsCounts,
  blockchainCounts =>
    blockchainCounts.reduce<BlockchainsCountsMap>((map, { count, name }) => {
      map[mapSharkChainToChainPath(name)] = Number(count);

      return map;
    }, {}),
);

export const selectPrivateCountriesCountsMap = createSelector(
  selectPrivateCountriesCounts,
  countriesCounts =>
    countriesCounts.reduce<CountriesCountsMap>(
      (countryMap, { count, name: country }) => ({
        ...countryMap,
        [country]: { country, requests: Number(count) },
      }),
      {},
    ),
);

export const selectPrivateUsageTimeline = createSelector(
  selectPrivateStats,
  ({ request_timelines }) => {
    const usageTimeline = request_timelines.find(
      ({ name }) => name === 'Usage',
    );

    return usageTimeline?.points ?? timelineFallback;
  },
);

export const selectPrivateUsageTimelineMap = createSelector(
  selectPrivateUsageTimeline,
  usageTimeline => {
    const usageTimelineMap = usageTimeline.reduce<UsageTimelineMap>(
      (history, point) => {
        const timestamp = new Date(point.time).getTime();

        return { ...history, [timestamp]: Number(point.value) };
      },
      {},
    );

    return usageTimelineMap ?? usageTimelineMapFallback;
  },
);

export const selectPrivateTotalRequests = createSelector(
  selectPrivateStats,
  ({ total }) => Number(total),
);

export const selectPrivateBlockchainCountByChainID = createSelector(
  selectPrivateBlockchainsCounts,
  (state: RootState, _params: IFetchPrivateStatsParams, chainId: ChainID) => ({
    chainId,
    state,
  }),
  (blockchainsCounts, { chainId, state }) => {
    const chainPaths = selectAllPathsByChainId(state, chainId);

    return sumBlockchainsCountsByChainPaths({
      blockchainsCounts,
      chainPaths,
    });
  },
  {
    argsMemoize: weakMapMemoize,
    memoize: weakMapMemoize,
    memoizeOptions: { resultEqualityCheck: deepEqulityCheck },
  },
);

export const selectPrivateAggregatedBlockchainsCountsByChainID = createSelector(
  selectPrivateBlockchainsCounts,
  (state: RootState) => state,
  (blockchainsCounts, state) =>
    aggregateBlockchainsCountsByChainID(state, blockchainsCounts),
);
